// Client-side thumbnail generation for uploaded assets.
//
// Three-tier strategy, cheapest first:
//   1. Images (PNG/JPG/GIF/WebP/BMP) → HTMLImageElement → canvas → JPEG.
//      Pure browser, no WASM.
//   2. Browser-decodable video (MP4/H.264, WebM/VP9, HEVC on supporting
//      browsers, etc.) → HTMLVideoElement → canvas → JPEG. Pure browser.
//   3. Exotic codecs the browser can't decode (HAP, DNxHD, ProRes, MKV
//      with unusual codecs, etc.) → our self-hosted LGPL ffmpeg.wasm.
//      Loaded lazily on the first fallback, then reused.
//
// The wasm is the LGPL build at state-stream/wasm-build/ (NOT the
// official @ffmpeg/core npm package, which is GPL-2.0-or-later and
// would taint a closed-source product per CLAUDE.md). It's hosted in
// our R2 bucket and served via the Worker's /static/ffmpeg-core/ route.
//
// Returns `{blob, width, height, codec}` on success or `null` if every
// path fails — the upload still completes; the asset just won't have a
// poster until on-prem thumbnailing runs.

import { state } from '@/stores/deluge.js';

const TARGET_WIDTH = 320;
const JPEG_QUALITY = 0.82;

let ffmpegInstance = null;
let ffmpegLoadPromise = null;

const IMAGE_EXTS = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp'];
const VIDEO_EXTS = ['.mp4', '.mov', '.webm', '.mkv', '.avi', '.hap'];

function lower(name) {
  return name.toLowerCase();
}
function hasExt(name, list) {
  const l = lower(name);
  return list.some((ext) => l.endsWith(ext));
}

/**
 * Generate a thumbnail AND probe basic media metadata.
 * Returns `{blob, width, height, codec}` on success or `null`.
 */
export async function generateThumbnail(file) {
  try {
    if (hasExt(file.name, IMAGE_EXTS)) {
      return await thumbnailFromImage(file);
    }
    if (hasExt(file.name, VIDEO_EXTS)) {
      return await thumbnailFromVideo(file);
    }
    return (
      (await thumbnailFromImage(file).catch(() => null)) ||
      (await thumbnailFromVideo(file).catch(() => null))
    );
  } catch (e) {
    console.warn('[thumbnail] generation failed:', e);
    return null;
  }
}

// ---------------------------------------------------------------------------
// Images — pure browser, no WASM.
// ---------------------------------------------------------------------------

function codecFromName(name) {
  const lower = (name || '').toLowerCase();
  const dot = lower.lastIndexOf('.');
  return dot >= 0 ? lower.slice(dot + 1) : '';
}

async function thumbnailFromImage(file) {
  const url = URL.createObjectURL(file);
  try {
    const img = await loadImage(url);
    const blob = await drawToJpegBlob(img, img.naturalWidth, img.naturalHeight);
    if (!blob) return null;
    return {
      blob,
      width: img.naturalWidth,
      height: img.naturalHeight,
      codec: codecFromName(file.name),
    };
  } finally {
    URL.revokeObjectURL(url);
  }
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = (e) => reject(new Error('image load failed: ' + e));
    img.src = src;
  });
}

async function drawToJpegBlob(source, srcW, srcH) {
  const ratio = srcH / srcW;
  const w = Math.min(TARGET_WIDTH, srcW);
  const h = Math.round(w * ratio);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(source, 0, 0, w, h);
  return await new Promise((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY)
  );
}

// ---------------------------------------------------------------------------
// Video — HTMLVideoElement first, fall back to LGPL ffmpeg.wasm.
// ---------------------------------------------------------------------------

async function thumbnailFromVideo(file) {
  // Cheap path: ask the browser to decode it. Works for everything the
  // user's browser natively supports — MP4/H.264, WebM/VP9, HEVC on
  // Safari, AV1 on recent Chromes, etc. ≥95% of digital signage uploads.
  const fast = await thumbnailViaVideoElement(file).catch(() => null);
  if (fast) return fast;

  // Slow path: LGPL ffmpeg.wasm. Covers HAP, DNxHD, ProRes, MKV with
  // unusual codecs, etc. ~15-20 MB one-time download, cached forever.
  return await thumbnailViaFfmpeg(file);
}

async function thumbnailViaVideoElement(file) {
  const url = URL.createObjectURL(file);
  try {
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    video.src = url;
    await new Promise((resolve, reject) => {
      let settled = false;
      const ok = () => {
        if (settled) return;
        settled = true;
        resolve();
      };
      const fail = (e) => {
        if (settled) return;
        settled = true;
        reject(new Error('video element failed: ' + (e?.message || e)));
      };
      video.addEventListener('loadeddata', ok, { once: true });
      video.addEventListener('error', fail, { once: true });
      setTimeout(() => fail(new Error('timeout')), 4000);
    });
    // Seek slightly forward so we don't grab a black first frame.
    video.currentTime = Math.min(0.5, (video.duration || 1) * 0.1);
    await new Promise((resolve, reject) => {
      video.addEventListener('seeked', resolve, { once: true });
      video.addEventListener('error', reject, { once: true });
      setTimeout(() => reject(new Error('seek timeout')), 4000);
    });
    const blob = await drawToJpegBlob(video, video.videoWidth, video.videoHeight);
    if (!blob) return null;
    return {
      blob,
      width: video.videoWidth,
      height: video.videoHeight,
      codec: codecFromName(file.name),
    };
  } finally {
    URL.revokeObjectURL(url);
  }
}

async function thumbnailViaFfmpeg(file) {
  const ffmpeg = await loadFfmpeg();
  // Mount the File via WORKERFS so ffmpeg can seek into it without
  // copying. Unique mount path per call so concurrent uploads don't
  // collide.
  const mountPath = '/mnt' + Math.random().toString(36).slice(2, 10);
  await ffmpeg.mount('WORKERFS', { files: [file] }, mountPath);
  const inputPath = mountPath + '/' + file.name;
  const outputPath = 'thumb-' + Math.random().toString(36).slice(2, 10) + '.jpg';
  try {
    const rc = await ffmpeg.exec([
      '-hide_banner',
      '-loglevel', 'error',
      '-i', inputPath,
      '-vframes', '1',
      '-vf', `scale=${TARGET_WIDTH}:-1`,
      '-q:v', '3',
      // Forced mjpeg muxer — implicit image2 hangs in ffmpeg.wasm.
      '-f', 'mjpeg',
      outputPath,
    ]);
    if (rc !== 0) throw new Error('ffmpeg exec rc=' + rc);
    const data = await ffmpeg.readFile(outputPath);
    const blob = new Blob([data], { type: 'image/jpeg' });
    // ffmpeg.wasm doesn't ship ffprobe; the thumbnail's own dimensions
    // are scaled (320×n) so they're not useful as "source resolution".
    return { blob, width: 0, height: 0, codec: codecFromName(file.name) };
  } finally {
    try { await ffmpeg.deleteFile(outputPath); } catch {}
    try { await ffmpeg.unmount(mountPath); } catch {}
  }
}

async function loadFfmpeg() {
  if (ffmpegInstance) return ffmpegInstance;
  if (ffmpegLoadPromise) return await ffmpegLoadPromise;
  ffmpegLoadPromise = (async () => {
    const [{ FFmpeg }, { toBlobURL }] = await Promise.all([
      import('@ffmpeg/ffmpeg'),
      import('@ffmpeg/util'),
    ]);
    // Our self-hosted LGPL ffmpeg.wasm — see state-stream/wasm-build/.
    // The wasm exceeds Cloudflare Pages' 25 MiB per-file limit so we
    // host it in R2 and proxy via the Worker, which sets a long
    // Cache-Control so each browser cache fetches it once.
    const base = state.devRedirect || '';
    const coreURL = await toBlobURL(
      base + '/static/ffmpeg-core/ffmpeg-core.js',
      'text/javascript',
    );
    const wasmURL = await toBlobURL(
      base + '/static/ffmpeg-core/ffmpeg-core.wasm',
      'application/wasm',
    );
    const ff = new FFmpeg();
    await ff.load({ coreURL, wasmURL });
    ffmpegInstance = ff;
    return ff;
  })();
  return await ffmpegLoadPromise;
}
